/**
 * AppRoutes — public + authenticated route trees and the session gate.
 * Extracted from App.tsx so the shell stays under the 500-line ceiling.
 */
import React, { useState, useEffect, useRef, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { tokens } from '../design-system/tokens';
import { LandingPage, marketingRoutes } from './marketingRoutes';
import GettingStarted from '../pages/GettingStarted';
import { useOnboardingGate } from './useOnboardingGate';
import { isPublicPath } from './publicPaths';
import { ShopifyInstallIntercept } from '../components/ShopifyInstallIntercept';
import { Login } from '../pages/Login';
import { Signup } from '../pages/Signup';
import { ForgotPassword } from '../pages/ForgotPassword';
import { ResetPassword } from '../pages/ResetPassword';
import { AuthCallback } from '../pages/AuthCallback';
import { VerifyEmail } from '../pages/VerifyEmail';

// Admin Panel

import PrivacyPolicy from '../pages/legal/PrivacyPolicy';
import PublicFormPage from '../pages/forms/PublicFormPage';
import PublicEventPage from '../pages/events/PublicEventPage';
import PublicBookingPage from '../pages/booking/PublicBookingPage';
import PublicRouterPage from '../pages/booking/PublicRouterPage';
import ManageBookingPage from '../pages/booking/ManageBookingPage';
import PublicBuzzPage from '../pages/buzzpage/PublicBuzzPage';
import PublicMemberPage from '../pages/members/PublicMemberPage';
import PublicPayPage from '../pages/pay/PublicPayPage';
import TermsOfService from '../pages/legal/TermsOfService';
import CookiePolicy from '../pages/legal/CookiePolicy';
import AcceptableUsePolicy from '../pages/legal/AcceptableUse';
import AccessibilityStatement from '../pages/legal/AccessibilityStatement';
import DataDeletionStatus from '../pages/legal/DataDeletionStatus';
import DataStatus from '../pages/legal/DataStatus';
import ClaudeDocs from '../pages/docs/ClaudeDocs';
import OAuthConsent from '../pages/oauth/Consent';
import OAuthAuthorize from '../pages/oauth/Authorize';
import DeviceConnect from '../pages/oauth/Connect';
import ConfirmAction from '../pages/assistant/ConfirmAction';
import ConnectedApps from '../pages/settings/ConnectedApps';
import api, { onApiError, setAuthToken } from '../services/api';
import { LEGACY_REDIRECTS } from '../routes/legacyRedirects';
import { LegacyRedirect } from '../routes/LegacyRedirect';

import { PageLoader } from './PageLoader';
import { RouteContentFallback } from './RouteContentFallback';
import { RequireSession } from './RequireSession';
import { consumeIntentionalLogout, loginPathFor, safeRedirect } from './loginRedirect';
import { integrationSettingsRoutes } from './integrationSettingsRoutes';
import { adminRoutes } from './adminRoutes';
import { AppLayout } from './AppLayout';
import {
  Inbox,
  Popups,
  Settings,
  Onboarding,
  BuzzTalk,
  BusinessHub,
  Suppliers,
  Customers,
  Orders,
  Products,
  Team,
  Brand,
  Billing,
  EmailDomainSettings,
  ProductAssets,
  VendorDashboard,
  AgentCanvas,
  Automations,
  AutomationsLab,
  SocialAutomator,
  EmailMarketing,
  FlowCanvas,
  AutomationDetail,
  CustomDomain,
  KnowledgeBase,
  PrivacyCenter,
  WhiteLabel,
  CallPage,
  AcceptInvite,
  AcceptChatInvite,
} from './lazyRoutes';

export const AppRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  // Whether this path renders without a session — see app/publicPaths.
  const isPublic = isPublicPath(location.pathname);

  // Shopify App Store install handoff: the OAuth callback redirects to
  // /signup?shopify=connected&shop=… with the token parked in Redis. An
  // already-authenticated visitor must still reach Signup (which finalizes the
  // connection) instead of being bounced to /business with the params dropped (F-2).
  const isShopifyConnectHandoff =
    new URLSearchParams(location.search).get('shopify') === 'connected';

  // Owner/admin-only workspace-setup gate (extracted — see useOnboardingGate).
  const onboardingChecked = useOnboardingGate(isPublic);

  // Avatar Logout may reach /login before the protected-tree guard runs;
  // drop the flag so a later cold visit to an app path still gets ?redirect=.
  useEffect(() => {
    if (location.pathname === '/login') consumeIntentionalLogout();
  }, [location.pathname]);

  // Auth gate. A token's PRESENCE is not proof it is real — the browser can't
  // verify the JWT signature, so a forged/revoked token would render the app
  // shell for a moment before the first API call bounced it. For protected
  // routes we therefore VERIFY the token against the server (`/auth/me`)
  // before rendering, and treat a server rejection as logged-out. Verified
  // per-token (ref), so ordinary in-app navigation never re-hits the API.
  const verifiedTokenRef = useRef<string | null>(null);
  useEffect(() => {
    const token = api.getAuthToken(); // already null for expired/malformed
    if (!token) {
      // An expired access token with a live refresh token is a RENEWABLE
      // session, not a logout. Renew before deciding — otherwise a user who
      // parked a tab past the 2h TTL (e.g. on /terms) came back "logged out"
      // while holding a perfectly valid 7-day refresh token.
      if (api.hasRefreshToken()) {
        let cancelled = false;
        setIsAuthenticated(null);
        api.refreshSession().then((renewed: boolean) => {
          if (cancelled) return;
          verifiedTokenRef.current = renewed ? api.getAuthToken() : null;
          setIsAuthenticated(renewed);
        });
        return () => { cancelled = true; };
      }
      verifiedTokenRef.current = null;
      setIsAuthenticated(false);
      return;
    }
    // Public routes don't gate on the server — the Navbar's own token check is
    // enough and we must not spin the landing page waiting on an API call.
    if (isPublic || verifiedTokenRef.current === token) {
      setIsAuthenticated(true);
      return;
    }
    let cancelled = false;
    setIsAuthenticated(null); // show the loading gate while we verify
    api.auth.me()
      .then(() => {
        if (cancelled) return;
        verifiedTokenRef.current = token;
        setIsAuthenticated(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Only a REAL rejection (401/403) means the token is forged/revoked —
        // drop it so /login can't redirect back into a verify loop. Transient
        // failures (network, 429 rate limit, 5xx, Render cold start) must NOT
        // log the user out: keep the token, render the app, and let each
        // page's own API calls surface their errors. Next navigation with a
        // different token re-verifies.
        const status = (err as { status?: number } | null)?.status;
        if (status === 401 || status === 403) {
          verifiedTokenRef.current = null;
          setAuthToken(null);
          setIsAuthenticated(false);
        } else {
          setIsAuthenticated(true);
        }
      });
    return () => { cancelled = true; };
  }, [location.pathname, isPublic]);

  // Global 401 handler: the API layer clears the token when a session expires
  // and refresh fails, but without this listener the user was left stranded on
  // a now-broken page (token gone, components erroring) until they manually
  // navigated. Bounce them to the correct login screen instead. Registered once;
  // reads the live pathname to avoid a stale closure.
  useEffect(() => {
    const unsubscribe = onApiError((error) => {
      if (error.status !== 401 || error.code !== 'UNAUTHORIZED') return;
      const path = window.location.pathname;
      if (path.startsWith('/admin')) {
        if (path !== '/admin/login') navigate('/admin/login', { replace: true });
        return;
      }
      // Public auth pages don't need a bounce — they already render logged-out.
      const publicAuthPaths = ['/', '/login', '/signup', '/forgot-password', '/reset-password'];
      if (publicAuthPaths.includes(path)) return;
      navigate('/login', { replace: true });
    });
    return unsubscribe;
  }, [navigate]);

  // What the pages that need a session go by. A sign-in that has just happened
  // stored its token, but the check above answers only after this render —
  // so the state still says the `false` it held on /login. Read as "logged
  // out", that sent a merchant who had just signed in back to /login and on to
  // /business, and the Shopify return they were signing in for was lost (QA on
  // #940). A token present but not yet checked is "not known yet": wait for
  // the check. The sign-in routes keep the raw state.
  const session: boolean | null = isAuthenticated === false && api.getAuthToken() ? null : isAuthenticated;

  // These surfaces skip the full-page spinner so a refresh does not flash a
  // centered loader over the existing UI. Auth still resolves; a confirmed
  // logged-out session redirects below. Other protected routes keep waiting.
  const path = location.pathname;
  const skipsFullPageLoader =
    path === '/account' || path === '/business' || path === '/inbox' || path === '/chat' || path === '/studio' || path === '/calendar';

  // Show loading state while checking authentication or onboarding
  if ((session === null || !onboardingChecked) && !isPublic && !skipsFullPageLoader) {
    const loadingContainerStyles: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: tokens.colors.gray[50],
    };

    const spinnerStyles: React.CSSProperties = {
      width: '48px',
      height: '48px',
      border: `2px solid ${tokens.colors.gray[200]}`,
      borderTop: `2px solid ${tokens.colors.accent[600]}`,
      borderRadius: tokens.borderRadius.full,
      animation: 'spin 1s linear infinite',
    };

    return (
      <>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={loadingContainerStyles}>
          <div style={spinnerStyles}></div>
        </div>
      </>
    );
  }

  if (isPublic) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={
            <ShopifyInstallIntercept>
              {/* The landing always renders on "/" — logged-in users stay here on
                refresh instead of being bounced into the app. The navbar shows a
                "Dashboard" link when authenticated so they can still get in. */}
              <LandingPage />
            </ShopifyInstallIntercept>
          } />
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/auth" element={<Navigate to="/login" replace />} />
          {/* When a Shopify App Store install hands off (?shopify=connected&shop=…),
            render Signup even for an already-authenticated visitor so it can
            finalize the parked connection — otherwise the redirect to /business
            drops the params and the store silently never links (F-2). */}
          {/* Already signed in: continue to the link's ?redirect= when it is a
            same-site path (a Shopify return carried through sign-in), else /business.
            While the session is still resolving, show the form — a centered
            loader on refresh made these public auth pages feel stuck. */}
          <Route path="/login" element={
            // Live token check: after Logout, React state can still say
            // authenticated for one paint — without this the form bounces to
            // /business and the tab title/favicon flash with it.
            isAuthenticated && !!api.getAuthToken() && !isShopifyConnectHandoff
              ? <Navigate to={safeRedirect(new URLSearchParams(location.search).get('redirect'))} replace />
              : <Login />
          } />
          <Route path="/signup" element={
            isAuthenticated && !!api.getAuthToken() && !isShopifyConnectHandoff
              ? <Navigate to="/business" replace />
              : <Signup />
          } />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Needs a session although it is public (no app shell): waits while
            the session resolves, and a logged-out visitor keeps the Shopify
            return (?platform=shopify&success=true&shop=…) through sign-in. */}
          <Route path="/onboarding" element={
            <RequireSession isAuthenticated={session}><Onboarding /></RequireSession>
          } />
          <Route path="/vendor/:accessKey" element={<VendorDashboard />} />
          {/* Public docs */}
          <Route path="/docs/claude" element={<ClaudeDocs />} />
          <Route path="/docs/mcp" element={<ClaudeDocs />} />
          <Route path="/docs/plugin" element={<ClaudeDocs />} />
          {/* OAuth 2.0 consent screen (Phase 6) */}
          <Route path="/oauth/consent" element={<OAuthConsent />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
          <Route path="/connect" element={<DeviceConnect />} />
          {/* BuzzCall (W2): public call page — guests join by magic link */}
          <Route path="/call/:token" element={<CallPage />} />
          {/* Team invites: emailed accept page (server routes/team.ts builds the URL) */}
          <Route path="/invite/:token" element={<AcceptInvite />} />
          {/* BuzzTalk guests: emailed magic link (server chatGuestService builds the
            URL). More specific than the authenticated /chat/:groupId below, so
            react-router ranks it first. */}
          <Route path="/chat/accept/:token" element={<AcceptChatInvite />} />
          {/* Board Forms: public capture page — submissions become board rows */}
          <Route path="/f/:slug" element={<PublicFormPage />} />
          {/* Events (V1a): public registration page */}
          <Route path="/e/:slug" element={<PublicEventPage />} />
          {/* Native booking page (V1b): public slot booking */}
          <Route path="/book/:slug" element={<PublicBookingPage />} />
          {/* The routing form: one question, then /book/<slug>. */}
          <Route path="/r/:slug" element={<PublicRouterPage />} />
          {/* Customer's own cancel/reschedule link — the token IS the credential */}
          <Route path="/booking/:token" element={<ManageBookingPage />} />
          {/* BuzzPage (V3a): public link-in-bio front door */}
          <Route path="/p/:slug" element={<PublicBuzzPage />} />
          {/* Members area (V4): magic-link lesson pages */}
          <Route path="/m/:token" element={<PublicMemberPage />} />
          {/* Branded checkout (PAY-2): merchant-branded payment page */}
          <Route path="/pay/thanks" element={<PublicPayPage />} />
          <Route path="/pay/:token" element={<PublicPayPage />} />
          {/* Getting Started guide — public, linked from footer + in-app (T10) */}
          <Route path="/getting-started" element={<GettingStarted />} />
          {/* Legal Pages (Public - Required for Meta App Review) */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/cookies" element={<CookiePolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUsePolicy />} />
          {/* הצהרת נגישות — linked from the floating accessibility widget on every page */}
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          {/* Meta App Review: Data Deletion & Deauthorize Status Pages */}
          <Route path="/data-deletion-status" element={<DataDeletionStatus />} />
          <Route path="/data-status" element={<DataStatus />} />
          {/* Shopify Embedded App Routes — disabled (standalone app, not embedded) */}
          {/* Admin panel route tree — ./app/adminRoutes.tsx. Interpolated, not
            rendered as a component: <Routes> rejects non-Route children. */}
          {adminRoutes}
          {marketingRoutes}

          {/* Catch all - redirect to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    );
  }

  // Redirect unauthenticated users to login, remembering where they were
  // going. The Shopify OAuth callback sends a logged-out merchant to
  // /settings/integrations?shopify=success&shop=…; without `redirect=` that
  // landing (and its toast) was lost the moment the login form appeared.
  // While session is still null on a skip-loader path, keep the page up (no
  // spinner / no premature bounce) until the check finishes.
  if (session === false || (session === null && !skipsFullPageLoader)) {
    // Intentional Logout clears tokens then navigates to /login; if App
    // re-renders on the old app path first, skip ?redirect= so the URL stays
    // bare /login (no hard reload, no metadata flicker).
    const to = consumeIntentionalLogout()
      ? '/login'
      : loginPathFor(location.pathname, location.search);
    return <Navigate to={to} replace />;
  }

  return (
    <AppLayout>
      <Suspense fallback={<RouteContentFallback pathname={location.pathname} />}>
        <Routes>
          {/* Landing → Business Hub (the old merchant Dashboard page has been removed) */}
          <Route path="/" element={<Navigate to="/business" replace />} />

          {/* BuzzTalk — internal team chat */}
          <Route path="/chat" element={<BuzzTalk />} />
          <Route path="/chat/:groupId" element={<BuzzTalk />} />

          {/* The /agent hub ("Unified Agent") was removed. The flow CANVAS stays for
              chat-flow editing; the hub + its legacy redirects are gone. */}
          <Route path="/agent/canvas/:flowId" element={<AgentCanvas />} />
          <Route path="/agent" element={<Navigate to="/studio" replace />} />
          <Route path="/support" element={<Navigate to="/inbox" replace />} />

          {/* Cross-app Automations Lab (Chat · Builder canvas · Active · Templates)
              is now the primary automations surface. The previous single-canvas
              builder stays reachable at /automations/classic (fallback) and powers
              per-flow editing at /automations/:id, so nothing is lost. */}
          <Route path="/automations" element={<AutomationsLab />} />
          <Route path="/automations/lab" element={<AutomationsLab />} />
          <Route path="/automations/classic" element={<Automations />} />
          <Route path="/automations/:id" element={<Automations />} />

          {/* LIVE-16 (2026-05-31): the previous CommandCenter component
              shipped a "Visual flow automation designer coming soon..." stub
              to production. Until that work lands, redirect /command-center
              to /automations (the actual working canvas). Re-add the
              CommandCenter import + the original <CommandCenter /> route
              when real content ships. */}
          <Route path="/command-center" element={<Navigate to="/automations" replace />} />

          {/* Business Hub - Unified 6-Tab Command Center */}
          <Route path="/business" element={<BusinessHub />} />

          {/* Business Hub sub-routes */}
          <Route path="/business/customers" element={<Customers />} />
          {/* Customer profile by id — both /customers/:id and /contacts/:id
              resolve to the Customers page; the drawer opens if the customer
              is selected. Previously these URLs hit the SPA fallback and
              redirected to /dashboard, which surfaced as B-N09. */}
          <Route path="/business/customers/:id" element={<Customers />} />
          <Route path="/business/contacts" element={<Customers />} />
          <Route path="/business/contacts/:id" element={<Customers />} />
          <Route path="/business/orders" element={<Orders />} />
          <Route path="/business/products" element={<Products />} />
          <Route path="/business/suppliers" element={<Suppliers />} />

          {/* Email Marketing — Design-mandated routes */}
          <Route path="/studio" element={<EmailMarketing />} />
          {/* The deep link every host and the extension send the owner to for a parked send. */}
          <Route path="/assistant/confirm" element={<ConfirmAction />} />
          <Route path="/studio/flow/:flowId" element={<FlowCanvas />} />
          <Route path="/studio/automation/:id" element={<AutomationDetail />} />
          <Route path="/calendar" element={<SocialAutomator />} /><Route path="/marketing/social" element={<SocialAutomator />} />
          <Route path="/marketing/ab-tests" element={<Navigate to="/studio?tab=campaigns" replace />} />
          <Route path="/popups" element={<Popups />} />

          {/* White-Label */}
          <Route path="/settings/custom-domain" element={<CustomDomain />} />
          <Route path="/settings/white-label" element={<WhiteLabel />} />
          <Route path="/settings/email-domain" element={<EmailDomainSettings />} />

          {/* Knowledge Base */}
          <Route path="/knowledge-base" element={<KnowledgeBase />} />

          {/* GDPR Privacy Center */}
          <Route path="/privacy-center" element={<PrivacyCenter />} />

          {/* Account — unified settings hub (absorbs Brand DNA + Integrations as tabs).
              /brand-dna and /settings/integrations redirect here via legacyRedirects. */}
          <Route path="/account" element={<Settings />} />

          {/* Integration detail / OAuth pages (reached from the Account >
              Integrations tab) — see app/integrationSettingsRoutes.tsx. */}
          {integrationSettingsRoutes}

          {/* Brand submenu routes */}
          <Route path="/settings/brand" element={<Brand />} />
          <Route path="/settings/brand/team" element={<Team />} />
          <Route path="/settings/brand/assets" element={<ProductAssets />} />
          <Route path="/settings/billing" element={<Billing />} />
          {/* What can reach the workspace as an AI app (the device grant, a
              browser consent, a key), and the revoke that stops it. */}
          <Route path="/settings/connected-apps" element={<ConnectedApps />} />

          {/* Additional Active Routes */}
          <Route path="/inbox" element={<Inbox />} />
          {/* /inbox-view + /campaigns removed 2026-05-31 (LIVE-14, LIVE-15
              dup-route dedup). They're handled via legacyRedirects.ts now:
              /inbox-view → /inbox, /campaigns → /studio?tab=campaigns. */}
          <Route path="/marketing" element={<Navigate to="/marketing/social" replace />} />

          {/* Legacy redirects — centralized in routes/legacyRedirects.ts */}
          {LEGACY_REDIRECTS.map(r => (
            <Route key={r.from} path={r.from} element={<LegacyRedirect to={r.to} />} />
          ))}

          {/* To /business, NOT to "/".
            *
            * "/" is a PUBLIC path (app/publicPaths.ts) and deliberately renders
            * the marketing Landing for signed-in visitors too, so they are not
            * bounced out of it on refresh. That makes it the one destination an
            * authenticated fallback must never use: navigating here flips
            * `isPublic` to true, the public tree renders, and the route two
            * screens up that maps "/" to /business never gets the chance to
            * run. A signed-in merchant who mistyped a URL — or followed
            * /workspace, which is the LABEL of a nav item whose real route is
            * /chat — landed on "Sign In / Start Free" with a perfectly valid
            * session, and no 404 to explain it.
            */}
          <Route path="*" element={<Navigate to="/business" replace />} />
        </Routes>
      </Suspense>
    </AppLayout>
  );
};

